// ─────────────────────────────────────────────────────────────────────────────
// NKEmpowerDriver — Web Bluetooth driver for the NK EmPower oarlock.
//
// GATT tree confirmed (Service A + Service B + standard services).
// dataStreamB (`12630003-…`) carries 19-byte per-stroke summary frames —
// parsed by `nkParseStrokeFrame.js` (reverse-engineered Feb 2026).
// ─────────────────────────────────────────────────────────────────────────────

import { parseStrokeFrame as parseNkStroke } from './nkParseStrokeFrame';

// ── Confirmed GATT tree (captured 2026-02 via nRF Connect on a real EmPower) ──
export const NK_SERVICES = {
  config: '85920000-0338-4b83-ae4a-ac1d217adb03',
  data:   '12630000-dd25-497d-9854-9b6c02c77054',
  battery: 'battery_service',
  deviceInfo: 'device_information',
  genericAccess: 'generic_access',
};

export const NK_CHARS = {
  // Service A — config / firmware
  configRead:     '8592ffff-0338-4b83-ae4a-ac1d217adb03',  // READ
  configIndicate: '85920100-0338-4b83-ae4a-ac1d217adb03',  // INDICATE, READ
  configWrite:    '85920200-0338-4b83-ae4a-ac1d217adb03',  // WRITE
  // Service B — data streams (one of these is force, one is angle, one is summary)
  dataStreamA:    '12630001-dd25-497d-9854-9b6c02c77054',  // NOTIFY, READ — high-freq #1
  dataSummary:    '12630002-dd25-497d-9854-9b6c02c77054',  // INDICATE, READ — per-stroke summary
  dataStreamB:    '12630003-dd25-497d-9854-9b6c02c77054',  // NOTIFY, READ — high-freq #2
  dataConfig:     '12630200-dd25-497d-9854-9b6c02c77054',  // READ — static config
};

// All four notifiable chars — used by Live Byte Logger to listen simultaneously.
export const NK_NOTIFIABLE_CHARS = [
  { key: 'configIndicate', uuid: NK_CHARS.configIndicate, service: NK_SERVICES.config, kind: 'indicate' },
  { key: 'dataStreamA',    uuid: NK_CHARS.dataStreamA,    service: NK_SERVICES.data,   kind: 'notify' },
  { key: 'dataSummary',    uuid: NK_CHARS.dataSummary,    service: NK_SERVICES.data,   kind: 'indicate' },
  { key: 'dataStreamB',    uuid: NK_CHARS.dataStreamB,    service: NK_SERVICES.data,   kind: 'notify' },
];

// Static streaming-UUID config (filled once the byte format is reverse-engineered).
const STREAM_GATT = {
  serviceUuid: null,
  forceCharUuid: null,
  angleCharUuid: null,
};

// Optional services declared up-front so getPrimaryServices() returns them.
const OPTIONAL_SERVICES = [
  NK_SERVICES.config,
  NK_SERVICES.data,
  NK_SERVICES.battery,
  NK_SERVICES.deviceInfo,
  NK_SERVICES.genericAccess,
];

const DEFAULT_NAME_FILTER = { namePrefix: 'EMPOWER' };  // matches "EMPOWER...C-1106171"

// Broader filter set used by requestDevice() — order matters: the chooser
// shows any device matching ANY filter. We accept:
//   1. NK's canonical "EMPOWER..." names
//   2. shorter "EMP-XXXX" advertisement names (firmware in pairing mode)
//   3. anything advertising the EmPower data service UUID (catches new firmwares
//      that change the display name)
const DISCOVERY_FILTERS = [
  { namePrefix: 'EMPOWER' },
  { namePrefix: 'EmPower' },
  { namePrefix: 'EMP-' },
  { namePrefix: 'NK-' },
  { services: ['12630000-dd25-497d-9854-9b6c02c77054'] },     // NK data service
  { services: ['85920000-0338-4b83-ae4a-ac1d217adb03'] },     // NK config service
];

export default class NKEmpowerDriver {
  constructor({ rigBySide = {}, nameFilter } = {}) {
    this.vendor = 'nk_empower';
    this.connected = false;
    this.rigBySide = rigBySide;
    this.nameFilter = nameFilter || DEFAULT_NAME_FILTER;
    this.listeners = new Set();
    this._statusListeners = new Set();
    this._device = null;
    this._server = null;
    this._notifyCleanups = [];
    this._logCleanups = [];
    this.lastTree = null;
    this.deviceInfo = null;        // populated by readDeviceInfo()
    this.batteryLevel = null;
  }

  static isSupported() {
    return typeof navigator !== 'undefined' && !!navigator.bluetooth;
  }

  async requestAndConnect(/* opts */) {
    // ── REMOVED 2026-06 ────────────────────────────────────────────────────
    // NK ecosystem is closed: both the EmPower oarlock and the SpeedCoach GPS
    // advertise ADV_SCAN_IND (non-connectable) and reject any third-party BLE
    // Central. The driver is kept in-tree for parser reuse only — connect is
    // intentionally a hard error so accidental wiring fails fast.
    throw new Error('NK EmPower is not supported — the NK ecosystem is closed (ADV_SCAN_IND). XBoat XPod is the active oarlock integration.');
  }

  async discoverGattTree() {
    if (!this._server || !this.connected) throw new Error('Not connected.');
    const services = await this._server.getPrimaryServices();
    const tree = [];
    for (const svc of services) {
      const node = { uuid: svc.uuid, isPrimary: svc.isPrimary, characteristics: [] };
      try {
        const chars = await svc.getCharacteristics();
        for (const ch of chars) {
          node.characteristics.push({
            uuid: ch.uuid,
            properties: {
              broadcast: ch.properties.broadcast,
              read: ch.properties.read,
              writeWithoutResponse: ch.properties.writeWithoutResponse,
              write: ch.properties.write,
              notify: ch.properties.notify,
              indicate: ch.properties.indicate,
              authenticatedSignedWrites: ch.properties.authenticatedSignedWrites,
              reliableWrite: ch.properties.reliableWrite,
              writableAuxiliaries: ch.properties.writableAuxiliaries,
            },
          });
        }
      } catch (e) {
        node.characteristicsError = e?.message || String(e);
      }
      tree.push(node);
    }
    this.lastTree = { capturedAt: Date.now(), services: tree };
    return this.lastTree;
  }

  /** READ each Device Information char + the Battery Level once. */
  async readDeviceInfo() {
    if (!this._server || !this.connected) throw new Error('Not connected.');
    const out = {};
    try {
      const di = await this._server.getPrimaryService(NK_SERVICES.deviceInfo);
      const fields = {
        manufacturer: '00002a29-0000-1000-8000-00805f9b34fb',
        model:        '00002a24-0000-1000-8000-00805f9b34fb',
        serial:       '00002a25-0000-1000-8000-00805f9b34fb',
        hardware:     '00002a27-0000-1000-8000-00805f9b34fb',
        firmware:     '00002a26-0000-1000-8000-00805f9b34fb',
        software:     '00002a28-0000-1000-8000-00805f9b34fb',
        systemId:     '00002a23-0000-1000-8000-00805f9b34fb',
      };
      for (const [k, uuid] of Object.entries(fields)) {
        try {
          const ch = await di.getCharacteristic(uuid);
          const v = await ch.readValue();
          if (k === 'systemId') {
            out[k] = Array.from(new Uint8Array(v.buffer)).map(b => b.toString(16).padStart(2, '0')).join(':');
          } else {
            out[k] = new TextDecoder().decode(v.buffer).replace(/\u0000+$/, '');
          }
        } catch (_e) { /* swallow per-field */ }
      }
    } catch (_e) { /* deviceInfo service missing */ }
    try {
      const bs = await this._server.getPrimaryService(NK_SERVICES.battery);
      const bl = await bs.getCharacteristic('00002a19-0000-1000-8000-00805f9b34fb');
      const v = await bl.readValue();
      out.batteryLevel = v.getUint8(0);
      this.batteryLevel = out.batteryLevel;
    } catch (_e) { /* swallow */ }
    this.deviceInfo = out;
    this._emitStatus();
    return out;
  }

  /**
   * Live Byte Logger — subscribe to ALL four notifiable chars simultaneously and
   * stream raw notifications to a callback. Used to reverse-engineer the byte
   * format empirically: do one stroke, see which char fires + what bytes look like.
   *
   * @param {(frame: { key, uuid, bytes: Uint8Array, hex: string, ts: number }) => void} onFrame
   * @returns {Promise<{ stop: () => void, status: Array<{ key, uuid, ok, error? }> }>}
   */
  async startLiveCapture(onFrame) {
    if (!this._server || !this.connected) throw new Error('Not connected.');
    const cleanups = [];
    const status = [];
    for (const { key, uuid, service } of NK_NOTIFIABLE_CHARS) {
      try {
        const svc = await this._server.getPrimaryService(service);
        const ch = await svc.getCharacteristic(uuid);
        const handler = () => {
          try {
            const dv = ch.value;
            if (!dv) return;
            const bytes = new Uint8Array(dv.buffer, dv.byteOffset, dv.byteLength);
            const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join(' ');
            onFrame({ key, uuid, bytes, hex, ts: Date.now() });
          } catch (_e) { /* swallow per-frame */ }
        };
        ch.addEventListener('characteristicvaluechanged', handler);
        await ch.startNotifications();
        cleanups.push(() => {
          try { ch.stopNotifications(); } catch (_e) { /* noop */ }
          ch.removeEventListener('characteristicvaluechanged', handler);
        });
        status.push({ key, uuid, ok: true });
      } catch (e) {
        // Some chars may not exist on every firmware — keep going.
        const errMsg = e?.message || String(e);
        status.push({ key, uuid, ok: false, error: errMsg });
        onFrame({ key, uuid, bytes: new Uint8Array(), hex: `(subscribe failed: ${errMsg})`, ts: Date.now(), error: true });
      }
    }
    this._logCleanups.push(...cleanups);
    const stop = () => {
      cleanups.forEach(fn => { try { fn(); } catch (_e) { /* noop */ } });
      // Filter out these cleanups from _logCleanups
      this._logCleanups = this._logCleanups.filter(fn => !cleanups.includes(fn));
    };
    return { stop, status };
  }

  /**
   * One-shot READ of a characteristic. Useful for the readable-only chars
   * (`configRead` 0x8592ffff, `dataConfig` 0x12630200) to inspect device
   * state without subscribing.
   */
  async readChar(serviceUuid, charUuid) {
    if (!this._server || !this.connected) throw new Error('Not connected.');
    const svc = await this._server.getPrimaryService(serviceUuid);
    const ch = await svc.getCharacteristic(charUuid);
    const dv = await ch.readValue();
    const bytes = new Uint8Array(dv.buffer, dv.byteOffset, dv.byteLength);
    const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join(' ');
    return { bytes, hex, length: bytes.length };
  }

  /**
   * Wake/probe write to the config-write characteristic (`85920200-…`).
   * Many NK firmwares stay asleep until a paired SpeedCoach polls them; this
   * sends a candidate "hello" payload to try to provoke the device into
   * streaming on the notifiable chars. The exact payload is unknown, so we
   * try a small set of common patterns (single zero byte, "ping" 0x01, then
   * a 4-byte session-start guess).
   *
   * @returns {Promise<{ attempts: Array<{ payload: string, ok: boolean, error?: string }> }>}
   */
  async probeWake() {
    if (!this._server || !this.connected) throw new Error('Not connected.');
    const svc = await this._server.getPrimaryService(NK_SERVICES.config);
    const ch = await svc.getCharacteristic(NK_CHARS.configWrite);
    const candidates = [
      { name: '0x00', bytes: new Uint8Array([0x00]) },
      { name: '0x01', bytes: new Uint8Array([0x01]) },
      { name: '0x01 0x00', bytes: new Uint8Array([0x01, 0x00]) },
      { name: '0xa5 0x01 0x00 0x00', bytes: new Uint8Array([0xa5, 0x01, 0x00, 0x00]) },
    ];
    const attempts = [];
    for (const c of candidates) {
      try {
        // writeValueWithoutResponse is fastest, but some firmwares require
        // writeValue (with response). Try with-response first for safety.
        if (typeof ch.writeValueWithResponse === 'function') {
          await ch.writeValueWithResponse(c.bytes);
        } else {
          await ch.writeValue(c.bytes);
        }
        attempts.push({ payload: c.name, ok: true });
      } catch (e) {
        attempts.push({ payload: c.name, ok: false, error: e?.message || String(e) });
      }
      // Small breather so the device has a chance to react before the next write
      await new Promise(r => setTimeout(r, 200));
    }
    return { attempts };
  }

  /**
   * Start streaming stroke summaries from `dataStreamB`. The 19-byte payload
   * is decoded by `parseStrokeFrame` and dispatched as:
   *   • `crewz-gate-metrics` — drives Peak Force / Catch Angle side metrics
   *     + Force Balance Indicator + per-side oarlock calibration aggregate
   *   • `crewz-rhythm-update` — drives the Rhythm Ratio (RHY) metric
   *
   * @param {object} opts
   * @param {'port'|'starboard'|'sweep'|'single_scull'} [opts.side='sweep']
   *   Which side this oarlock is installed on. Must match the saved
   *   OarlockConfig entry so per-side calibration applies.
   * @param {function} [opts.parseStrokeFrame]
   *   Override the default parser (used by tests / fuzz harnesses). The
   *   override must return an object with the same shape as
   *   `nkParseStrokeFrame`.
   */
  async startStream({ side = 'sweep', parseStrokeFrame = null } = {}) {
    if (!this._server || !this.connected) throw new Error('Not connected.');
    const parse = parseStrokeFrame || parseNkStroke;

    const svc = await this._server.getPrimaryService(NK_SERVICES.data);
    const ch = await svc.getCharacteristic(NK_CHARS.dataStreamB);

    let strokeCounter = 0;
    const handler = () => {
      try {
        const dv = ch.value;
        if (!dv) return;
        const parsed = parse(dv);
        if (!parsed) return;

        // ── Debug breadcrumb event — fires for EVERY decoded frame (valid or
        //    not) so the NK Debug Panel can show the raw → scaled → null state
        //    even on partial frames. Separate from the gate-metrics event so
        //    invalid frames don't pollute the main metric pipeline.
        try {
          const rawBytes = new Uint8Array(dv.buffer, dv.byteOffset, dv.byteLength);
          const rawHex = Array.from(rawBytes).map(b => b.toString(16).padStart(2, '0')).join(' ');
          window.dispatchEvent(new CustomEvent('crewz-nk-debug-frame', {
            detail: {
              side,
              parsed,
              rawHex,
              rawBytes: Array.from(rawBytes),
              t: Date.now(),
            },
          }));
        } catch (_e) { /* noop */ }

        if (!parsed.isValid) return;
        strokeCounter += 1;
        const ts = Date.now();

        // 1) Per-stroke calibration aggregate — consumed by
        //    useOarlockCalibration → ForceBalanceIndicator + side metrics.
        try {
          window.dispatchEvent(new CustomEvent('crewz-gate-metrics', {
            detail: {
              side,
              force_n: parsed.peakForceN,
              work_j: parsed.workPerStrokeJ,
              power_w: parsed.powerW,
              angle_deg: parsed.catchAngleDeg,
              stroke_count: strokeCounter,
              timestamp: ts,
              // Extras for downstream visualisers — non-breaking additions
              avg_force_n: parsed.avgForceN,
              finish_angle_deg: parsed.finishAngleDeg,
              effective_length_deg: parsed.effectiveLengthDeg,
              drive_length_cm: parsed.driveLengthCm,
              spm: parsed.spm,
              source: 'nk_empower',
            },
          }));
        } catch (_e) { /* noop */ }

        // 2) Rhythm Ratio — recovery / drive, clamped to sane range.
        if (parsed.driveTimeMs > 0 && parsed.recoveryTimeMs > 0) {
          const ratio = parsed.recoveryTimeMs / parsed.driveTimeMs;
          if (ratio >= 0.5 && ratio <= 10) {
            try {
              window.dispatchEvent(new CustomEvent('crewz-rhythm-update', {
                detail: {
                  ratio,
                  driveMs: parsed.driveTimeMs,
                  recoveryMs: parsed.recoveryTimeMs,
                  source: 'nk_empower',
                  t: ts,
                },
              }));
            } catch (_e) { /* noop */ }
          }
        }

        // 3) Internal `subscribe()` listeners — preserved for backward compat.
        const fakeForceArr = [parsed.peakForceN];
        const fakeAngleArr = [parsed.catchAngleDeg];
        this._emit(side, fakeForceArr, fakeAngleArr, strokeCounter);

        // 4) Debug breadcrumb when ?debug=1.
        try {
          if (typeof window !== 'undefined' &&
              window.location?.search?.includes('debug=1')) {
            // eslint-disable-next-line no-console
            console.log(
              `[nk] stroke #${strokeCounter} side=${side} ` +
              `PF=${parsed.peakForceN}N CA=${parsed.catchAngleDeg}° ` +
              `drive=${parsed.driveTimeMs}ms recovery=${parsed.recoveryTimeMs}ms ` +
              `SPM=${parsed.spm}`
            );
          }
        } catch (_e) { /* noop */ }
      } catch (_e) { /* swallow per-frame */ }
    };

    ch.addEventListener('characteristicvaluechanged', handler);
    await ch.startNotifications();

    this._streamSide = side;
    this._notifyCleanups.push(() => {
      try { ch.stopNotifications(); } catch (_e) { /* noop */ }
      ch.removeEventListener('characteristicvaluechanged', handler);
    });
  }

  /**
   * Streaming-unlock sequence — REMOVED 2026-06.
   *
   * NK ecosystem is closed: both the EmPower oarlock and the SpeedCoach GPS
   * advertise ADV_SCAN_IND and reject any third-party Central, so the
   * 6-step unlock + 750ms keepalive ritual previously documented here has
   * been removed (no path to reach it from a JS Central). The historical
   * mapping is preserved in git history (commit prior to the 2026-06 pivot)
   * in case NK ever publishes a Central-facing API.
   *
   * Kept as a hard-error throw so accidental wiring surfaces immediately.
   */
  async startUnlockSequence() {
    throw new Error('NK unlock sequence removed (2026-06 pivot). NK ecosystem is closed.');
  }

  _stopKeepalive() {
    // No keepalive to stop — retained as a no-op so older cleanup paths
    // (disconnect → _onDisconnected) keep their call sites valid.
  }

  /**
   * Send an arbitrary byte payload to the config-write characteristic
   * (`85920200-…`). Used by the Custom Hex Write panel so the rower can
   * iterate on candidate "start streaming" commands without a code change.
   *
   * @param {Uint8Array} bytes — payload to write (max 20 bytes for safety)
   * @param {boolean} withoutResponse — use writeValueWithoutResponse if true
   */
  async writeConfigBytes(bytes, { withoutResponse = false } = {}) {
    if (!this._server || !this.connected) throw new Error('Not connected.');
    if (!(bytes instanceof Uint8Array) || bytes.length === 0) {
      throw new Error('Payload must be a non-empty Uint8Array.');
    }
    const svc = await this._server.getPrimaryService(NK_SERVICES.config);
    const ch = await svc.getCharacteristic(NK_CHARS.configWrite);
    if (withoutResponse && typeof ch.writeValueWithoutResponse === 'function') {
      await ch.writeValueWithoutResponse(bytes);
    } else if (typeof ch.writeValueWithResponse === 'function') {
      await ch.writeValueWithResponse(bytes);
    } else {
      await ch.writeValue(bytes);
    }
    const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join(' ');
    return { hex, length: bytes.length };
  }

  async disconnect() {
    this._stopKeepalive();
    this._configWriteChar = null;
    [...this._notifyCleanups, ...this._logCleanups].forEach(fn => { try { fn(); } catch (_e) { /* noop */ } });
    this._notifyCleanups = [];
    this._logCleanups = [];
    if (this._device?.gatt?.connected) {
      try { this._device.gatt.disconnect(); } catch (_e) { /* noop */ }
    }
    this.connected = false;
    this._server = null;
    this._emitStatus();
    return true;
  }

  _onDisconnected = () => {
    this._stopKeepalive();
    this._configWriteChar = null;
    this.connected = false;
    this._server = null;
    this._emitStatus();
  };

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  subscribeStatus(listener) {
    this._statusListeners.add(listener);
    try { listener(this.getStatus()); } catch (_e) { /* noop */ }
    return () => this._statusListeners.delete(listener);
  }

  _emitStatus() {
    const status = this.getStatus();
    this._statusListeners.forEach(l => { try { l(status); } catch (_e) { /* noop */ } });
  }

  _emit(side, rawForceN, rawAngleDeg, strokeIndex) {
    const rig = this.rigBySide[side] || {};
    const zf = Number(rig.zf) || 0;
    const za = Number(rig.za) || 0;
    const forceN = rawForceN.map(f => Math.max(0, f - zf));
    const angleDeg = rawAngleDeg.map(a => a - za);
    const payload = { side, forceN, angleDeg, strokeIndex, timestamp: Date.now() };
    this.listeners.forEach(l => { try { l(payload); } catch (_e) { /* noop */ } });
  }

  getStatus() {
    return {
      connected: this.connected,
      vendor: this.vendor,
      deviceName: this._device?.name || null,
      deviceId: this._device?.id || null,
      stream: {
        // 19-byte dataStreamB parser is wired in; UUIDs are static.
        configured: true,
        serviceUuid: NK_SERVICES.data,
        characteristicUuid: NK_CHARS.dataStreamB,
        parser: 'nkParseStrokeFrame@v1',
        side: this._streamSide || null,
      },
      lastTree: this.lastTree,
      deviceInfo: this.deviceInfo,
      batteryLevel: this.batteryLevel,
    };
  }

  setRigBySide(rigBySide) {
    this.rigBySide = rigBySide || {};
  }
}
